// Phase 6 · customer channels. THE CONTRACT for the customer app (apps/customer), the website
// (apps/site) and the office screens for tickets, feedback, campaigns and rewards.
// Specified in 04 §4.8, 05 §5.3, 07 §7.1, 08, 15 §15.2–15.4 and 16 §16.7.
//
// What is defined where (all exported from `@ecobox/schema`):
//
//   customer.ts  (this file)
//     identity      customerUidSchema, customerAccountDocSchema (`customerAccounts/{uid}`),
//                   savedAddressSchema, savedConsigneeSchema (`customerAccounts/{uid}/savedAddresses|consignees`)
//     projection    customerShipmentDocSchema (`customerShipments/{customerUid}__{trackingNo}`)
//     callables     meResponseSchema, updateMeInputSchema, bookInputSchema, cancelBookingInputSchema,
//                   rescheduleBookingInputSchema, myShipmentsInputSchema/ResponseSchema,
//                   shareShipmentInputSchema, podUrlInputSchema/ResponseSchema, rateDeliveryInputSchema/
//                   ResponseSchema, openTicketInputSchema, replyTicketInputSchema,
//                   redeemVoucherInputSchema/ResponseSchema; CUSTOMER_CALLABLES lists their names
//     login         loginVerifyResponseSchema (`POST /v1/otp/verify` with purpose `login`)
//     feedback      feedbackDocSchema (`feedback/{fbk_…}`), feedbackScoreDocSchema (`feedbackScores/{dimension}__{key}`)
//     tickets       ticketDocSchema (`tickets/{tkt_…}`), ticketNoteDocSchema, ticketActionDocSchema
//                   (`tickets/{id}/notes|actions`, staff only)
//   rewards.ts    rewardsAccountDocSchema, rewardsLedgerDocSchema, voucherDocSchema, referralCodeDocSchema
//   whatsapp.ts   whatsappInboundDocSchema (`whatsappInbound/{messageId}`), whatsappContactDocSchema
//                 (`whatsappContacts/{customerUid}`), simulateWhatsappInboundInputSchema
//   customerConfig.ts  campaignSchema (`campaigns/{cmp_…}`), rewardsRulesSchema (`rewardsRules/current`),
//                 reviewLinkSchema (`reviewLinks/{sectorId}`): saved with `saveConfig` kinds
//                 `campaign`, `rewardsRules`, `reviewLink`
//   commands/customer.ts  the commands: openTicket, replyTicket, submitFeedback, reschedulePickup,
//                 redeemVoucher (customer principal); assignTicket, addTicketNote, resolveTicket,
//                 adjustRewards (staff)
//   money.ts      NOTIFICATION_TEMPLATES (the new templates) and `notificationDocSchema`
//   publicApi.ts  `campaignCode` on the pickup booking; purpose `login` on the one-time code
//
// Rules for everything a customer reads: the document carries `customerUid` (or is named by it),
// the rules compare it with the signed-in uid, and a list query must filter on it. A customer has
// NO staff claims. Nothing here is ever written by a client except the address book.
import { z } from 'zod';
import { countryOfPhone } from '@ecobox/domain';
import { e164Schema, trackingNoSchema, customerIdSchema } from './cargo';
import { branchCodeSchema, sectorCodeSchema, SERVICES } from './config';
import { campaignCodeSchema } from './customerConfig';
import { pickupIntentSchema, pickupWantsSchema, pickupWindowSchema, geoSchema } from './finalmile';

const iso = z.string();
const minor = z.number().int().nonnegative().max(1e12);

// ---- Identity ---------------------------------------------------------------------------------

/**
 * The Firebase Auth uid of a customer: `c_` and a keyed hash of the E.164 phone (never the phone
 * itself). The same phone always gives the same uid, so a consignee who has never logged in
 * already has their shipments waiting under it.
 */
export const customerUidSchema = z.string().regex(/^c_[A-Za-z0-9_-]{20,40}$/);

export const CUSTOMER_LANGUAGES = ['en', 'tl'] as const;
/** The values of "how did you find us?" with no default: a missing value means "unchanged". */
export const customerSourceValueSchema = z.enum([
  'walk_in',
  'referral',
  'agent',
  'website',
  'app',
  'social',
  'other',
]);
export const customerSourceSchema = customerSourceValueSchema;
export type CustomerSource = z.infer<typeof customerSourceSchema>;

/** Claims on a customer's Auth user. No `staff`, no `roleIds`, no `scope`. */
export interface CustomerClaims {
  customer: true;
  phoneE164: string;
}

export const referralCodeSchema = z.string().regex(/^[A-Z0-9]{6,10}$/, 'Referral code');

/** `customerAccounts/{customerUid}`: the profile the app shows and edits through `updateMe`. */
export const customerAccountDocSchema = z.object({
  uid: customerUidSchema,
  /** The `customers/{cus_…}` record this login is linked to (by phone). */
  customerId: customerIdSchema,
  phoneE164: e164Schema,
  name: z.string().max(120),
  /** A sector code (UAE, KSA, QA, PH, IN), or null until the welcome form is done. */
  country: sectorCodeSchema.nullable(),
  city: z.string().max(80),
  language: z.enum(CUSTOMER_LANGUAGES),
  /** "How did you find us?" (15 §15.2). Null until asked. */
  source: customerSourceSchema.nullable(),
  /** The campaign the customer came through (from the link), when there was one. */
  campaignCode: campaignCodeSchema.optional(),
  /** This customer's own referral code, to share. */
  referralCode: referralCodeSchema,
  /** The friend's code this customer typed. */
  referredByCode: referralCodeSchema.optional(),
  /** WhatsApp messages about their shipments (08). Off after "STOP". */
  whatsappOptIn: z.boolean(),
  createdAt: iso,
  updatedAt: iso,
  lastLoginAt: iso,
});
export type CustomerAccountDoc = z.infer<typeof customerAccountDocSchema>;

/** `customerAccounts/{uid}/savedAddresses/{id}`: the only customer data a client writes (07 §7.3). */
export const savedAddressSchema = z
  .object({
    label: z.string().trim().min(1).max(40),
    line1: z.string().trim().min(1).max(200),
    line2: z.string().max(200).default(''),
    city: z.string().max(80).default(''),
    landmark: z.string().max(200).default(''),
    geo: geoSchema.optional(),
    updatedAt: iso,
  })
  .strict();

/** `customerAccounts/{uid}/consignees/{id}`: the receiver address book. */
export const savedConsigneeSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    phoneE164: e164Schema,
    line1: z.string().trim().min(1).max(200),
    barangay: z.string().max(80).default(''),
    city: z.string().max(80).default(''),
    province: z.string().max(80).default(''),
    /** A `destinationCities` id. */
    cityId: z.string().max(80),
    landmark: z.string().max(200).default(''),
    relationship: z.string().max(40).default(''),
    updatedAt: iso,
  })
  .strict();

// ---- The customer's view of a shipment --------------------------------------------------------

export const CUSTOMER_SHIPMENT_ROLES = ['shipper', 'consignee', 'shared'] as const;
export type CustomerShipmentRole = (typeof CUSTOMER_SHIPMENT_ROLES)[number];

/** The id of a projection document: `{customerUid}__{trackingNo}`. */
export const customerShipmentId = (customerUid: string, trackingNo: string): string =>
  `${customerUid}__${trackingNo}`;

/**
 * `customerShipments/{customerUid}__{trackingNo}` (02 §2.3, 03 §3.8): what one person may see of one
 * shipment. Server-written by the trigger on shipments and boxes. It holds NO staff-only data, NO
 * phone number or street address, NO internal id beyond the tracking number (a box is its number
 * in the shipment, not its barcode), and the amounts only for the shipper.
 */
export const customerShipmentDocSchema = z
  .object({
    id: z.string(),
    customerUid: customerUidSchema,
    trackingNo: trackingNoSchema,
    role: z.enum(CUSTOMER_SHIPMENT_ROLES),
    service: z.enum(SERVICES),
    /** One of PUBLIC_STATUSES with EcoBox's own words. */
    status: z.object({ key: z.string(), label: z.string() }).strict(),
    originCity: z.string(),
    destinationCity: z.string(),
    boxCount: z.number().int().nonnegative(),
    boxes: z.array(
      z
        .object({
          /** 1..boxCount: the box's place in the shipment. */
          n: z.number().int().positive(),
          sizeId: z.string(),
          status: z.string(),
          statusLabel: z.string(),
        })
        .strict(),
    ),
    /** The other party's name: the consignee's for the shipper, the shipper's for the rest. */
    counterpart: z.string(),
    milestones: z.array(z.object({ key: z.string(), label: z.string(), at: iso }).strict()),
    /** When the shipment entered its current state (the latest milestone). */
    stateSince: iso.nullable(),
    predicted: z.object({ from: z.iso.date(), to: z.iso.date() }).strict().nullable(),
    /** Only for the shipper. */
    money: z
      .object({
        currency: z.string(),
        totalMinor: minor,
        paidMinor: minor,
        balanceMinor: z.number().int(),
        status: z.string(),
      })
      .strict()
      .nullable(),
    delivery: z
      .object({ at: iso, receiverName: z.string().max(80) })
      .strict()
      .nullable(),
    /** Delivery photos exist: ask `podUrl` for them. */
    hasPod: z.boolean(),
    /** This person's own rating, once given (also kept for the other party in their own document). */
    rating: z
      .object({ stars: z.number().int().min(1).max(5), comment: z.string().max(1000), at: iso })
      .strict()
      .nullable(),
    /** Masked numbers the shipper shared it with, like `+971 •••• 4567`. Only for the shipper. */
    sharedWith: z.array(z.string()).max(10),
    createdAt: iso,
    updatedAt: iso,
  })
  .strict();
export type CustomerShipmentDoc = z.infer<typeof customerShipmentDocSchema>;

/** `shipmentShares/{trackingNo}__{sharedUid}`: written by `shareShipment`, read by no client. */
export const shipmentShareDocSchema = z
  .object({
    trackingNo: trackingNoSchema,
    ownerUid: customerUidSchema,
    sharedUid: customerUidSchema,
    sharedPhoneE164: e164Schema,
    maskedPhone: z.string(),
    createdAt: iso,
  })
  .strict();

// ---- Login (POST /v1/otp/verify with purpose "login") -----------------------------------------

/**
 * The sign-in answer: a Firebase custom token for the verified phone (07 §7.1). Sign in with
 * `signInWithCustomToken`; the claims are `{customer: true, phoneE164}`.
 */
export const loginVerifyResponseSchema = z
  .object({
    ok: z.literal(true),
    customToken: z.string().min(20),
    uid: customerUidSchema,
    /** True when this login created the customer profile (so the app shows the welcome form). */
    isNewCustomer: z.boolean(),
  })
  .strict();
export type LoginVerifyResponse = z.infer<typeof loginVerifyResponseSchema>;

// ---- Callables (04 §4.8). Principal `customer:{uid}`. -----------------------------------------

export const CUSTOMER_CALLABLES = [
  'me',
  'updateMe',
  'book',
  'cancelBooking',
  'rescheduleBooking',
  'myShipments',
  'shareShipment',
  'podUrl',
  'rateDelivery',
  'openTicket',
  'replyTicket',
  'redeemVoucher',
] as const;
export type CustomerCallable = (typeof CUSTOMER_CALLABLES)[number];

export const customerProfileSchema = z
  .object({
    uid: customerUidSchema,
    customerId: customerIdSchema,
    phoneE164: e164Schema,
    name: z.string(),
    country: sectorCodeSchema.nullable(),
    city: z.string(),
    language: z.enum(CUSTOMER_LANGUAGES),
    source: customerSourceSchema.nullable(),
    campaignCode: campaignCodeSchema.optional(),
    referralCode: referralCodeSchema,
    referredByCode: referralCodeSchema.optional(),
    whatsappOptIn: z.boolean(),
  })
  .strict();

export const openBookingSchema = z
  .object({
    /** The pickup request id (`pur_…`). */
    reference: z.string(),
    status: z.string(),
    window: pickupWindowSchema,
    branchName: z.string(),
  })
  .strict();

/** `me {}`. */
export const meResponseSchema = z
  .object({
    ok: z.literal(true),
    profile: customerProfileSchema,
    /** Pickups asked for and not done yet. */
    bookings: z.array(openBookingSchema).max(20),
    rewards: z
      .object({
        balance: z.number().int(),
        pending: z.number().int(),
        lifetime: z.number().int(),
        tier: z.string(),
        /** What points can be spent on (the active entries of the rules' catalogue), for `redeemVoucher`. */
        catalogue: z
          .array(
            z
              .object({
                id: z.string(),
                name: z.string(),
                points: z.number().int().positive(),
                /** A fixed amount off in minor units, or null when the voucher is a percentage. */
                valueMinor: z.number().int().nullable(),
                percent: z.number().nullable(),
                currency: z.string().nullable(),
                validDays: z.number().int().positive(),
              })
              .strict(),
          )
          // A server that predates the catalogue sends none: the app then offers nothing to buy.
          .default([]),
      })
      .strict(),
    isNewCustomer: z.boolean(),
  })
  .strict();
export type MeResponse = z.infer<typeof meResponseSchema>;

/** `updateMe`: only what is sent changes. The country is never overwritten silently. */
export const updateMeInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    country: sectorCodeSchema.optional(),
    city: z.string().trim().max(80).optional(),
    language: z.enum(CUSTOMER_LANGUAGES).optional(),
    /** How they found us. */
    source: customerSourceSchema.optional(),
    /** A friend's referral code. Accepted once, and not your own. */
    referralCode: referralCodeSchema.optional(),
    /** The campaign code from the link they arrived through. */
    campaignCode: campaignCodeSchema.optional(),
    whatsappOptIn: z.boolean().optional(),
  })
  .strict();
export type UpdateMeInput = z.infer<typeof updateMeInputSchema>;

/** `book {…}`: a `requestPickup` with source `app`. Same shape as the website's `POST /v1/pickups`. */
export const bookInputSchema = z
  .object({
    /** Makes the request safe to send twice: the same id returns the same reference. */
    clientRequestId: z.string().regex(/^[A-Za-z0-9_-]{8,40}$/),
    branchId: branchCodeSchema,
    /** Defaults to the profile's name. */
    name: z.string().trim().min(2).max(120).optional(),
    address: z
      .object({
        line1: z.string().trim().min(3).max(200),
        line2: z.string().max(200).default(''),
        city: z.string().trim().min(2).max(80),
        landmark: z.string().max(200).default(''),
      })
      .strict(),
    geo: geoSchema.optional(),
    window: pickupWindowSchema,
    wants: pickupWantsSchema,
    notes: z.string().max(500).default(''),
    campaignCode: campaignCodeSchema.optional(),
    // What the customer wants to ship, kept on the pickup so the dispatcher and the driver can prefill.
    service: pickupIntentSchema.shape.service,
    destinationCityId: pickupIntentSchema.shape.destinationCityId,
    receiver: pickupIntentSchema.shape.receiver,
    /** Boxes by size (sea, air). */
    boxes: pickupIntentSchema.shape.boxes,
    /** Parcels by weight (Prime). */
    pieces: pickupIntentSchema.shape.pieces,
    voucherCode: pickupIntentSchema.shape.voucherCode,
    /** The estimate the customer saw, in minor units, and its currency. */
    estimateMinor: z.number().int().nonnegative().max(1e12).optional(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
  })
  .strict()
  .refine((b) => (b.estimateMinor === undefined) === (b.currency === undefined), {
    message: 'Give the estimate and its currency together',
    path: ['currency'],
  });
export type BookInput = z.infer<typeof bookInputSchema>;
export const bookResponseSchema = z
  .object({
    ok: z.literal(true),
    reference: z.string(),
    status: z.literal('requested'),
    repeat: z.boolean().optional(),
  })
  .strict();

export const cancelBookingInputSchema = z
  .object({ reference: z.string().min(1).max(40), reason: z.string().trim().min(3).max(300) })
  .strict();
export const rescheduleBookingInputSchema = z
  .object({ reference: z.string().min(1).max(40), window: pickupWindowSchema })
  .strict();
export const bookingChangeResponseSchema = z
  .object({ ok: z.literal(true), reference: z.string(), status: z.string() })
  .strict();

/** `myShipments {limit?, after?}`: newest first. Reads the projection. */
export const myShipmentsInputSchema = z
  .object({
    limit: z.number().int().min(1).max(100).default(50),
    /** The `updatedAt` of the last row of the previous page. */
    after: iso.optional(),
  })
  .strict();
export const myShipmentsResponseSchema = z
  .object({
    ok: z.literal(true),
    shipments: z.array(customerShipmentDocSchema),
    next: iso.nullable(),
  })
  .strict();

/** `shareShipment`: only the shipper shares, with a family phone number. */
export const shareShipmentInputSchema = z
  .object({
    trackingNo: trackingNoSchema,
    phoneE164: e164Schema,
    action: z.enum(['add', 'remove']).default('add'),
  })
  .strict();
export const shareShipmentResponseSchema = z
  .object({ ok: z.literal(true), sharedWith: z.array(z.string()) })
  .strict();

/**
 * `podUrl {trackingNo}`: the proof of delivery. In production each file has a short-lived signed
 * `url`. In the emulator there is no signed URL: read `path` with the Storage SDK, under the
 * customer's own sign-in (the Storage rules let a customer read the proof of their own shipments).
 */
export const podUrlInputSchema = z.object({ trackingNo: trackingNoSchema }).strict();
export const podUrlResponseSchema = z
  .object({
    ok: z.literal(true),
    files: z.array(
      z
        .object({
          kind: z.enum(['photo', 'signature']),
          path: z.string(),
          url: z.string().url().optional(),
          expiresAt: iso.optional(),
        })
        .strict(),
    ),
    receiverName: z.string(),
    deliveredAt: iso,
  })
  .strict();

/**
 * What is being rated: `collection` (the pickup or the counter, asked right after), `delivery` (the
 * delivery, asked of the consignee and the shipper when fully delivered) and `service` (the whole
 * service, asked of the shipper when it is delivered). One rating per person, shipment and kind.
 */
export const RATING_KINDS = ['collection', 'delivery', 'service'] as const;
export const ratingSchema = z.number().int().min(1).max(5);

/** `rateDelivery`: 1–5 stars and an optional comment, for the delivery or for the whole service. */
export const rateDeliveryInputSchema = z
  .object({
    trackingNo: trackingNoSchema,
    stars: ratingSchema,
    comment: z.string().trim().max(1000).default(''),
    kind: z.enum(RATING_KINDS).optional(),
    photos: z.array(z.string().max(200)).max(5).default([]),
    /** The customer agrees that a 4 or 5 star comment may be shown on the website with their first name. */
    publishConsent: z.boolean().default(false),
  })
  .strict();
export const rateDeliveryResponseSchema = z
  .object({
    ok: z.literal(true),
    feedbackId: z.string(),
    /** 4–5 stars: where to leave a public review (per country), when one is set. */
    reviewUrl: z.string().url().nullable(),
    /** 1–3 stars, or a complaint: the ticket opened. */
    ticketId: z.string().nullable(),
    message: z.string(),
  })
  .strict();

// ---- Feedback (15 §15.3) ----------------------------------------------------------------------

export const FEEDBACK_SOURCES = ['app', 'whatsapp', 'staff'] as const;

/** `feedback/{fbk_…}`: one rating from one person about one shipment. Staff read it within scope. */
export const feedbackDocSchema = z.object({
  id: z.string().regex(/^fbk_[0-9A-HJKMNP-TV-Z]{26}$/),
  kind: z.enum(RATING_KINDS),
  stars: ratingSchema,
  comment: z.string().max(1000),
  photos: z.array(z.string()).max(5),
  source: z.enum(FEEDBACK_SOURCES),
  customerUid: customerUidSchema,
  customerId: customerIdSchema.optional(),
  role: z.enum(['shipper', 'consignee', 'shared']),
  trackingNo: trackingNoSchema,
  /** The branch that took the shipment in (the rating counts for it) and its country. */
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  /** The destination branch that served it, the route, the partner and the driver it counts for. */
  servingBranchId: branchCodeSchema.optional(),
  routeCode: z.string().optional(),
  partnerId: z.string().optional(),
  driverUid: z.string().optional(),
  complaint: z.boolean(),
  ticketId: z.string().optional(),
  /** 4–5 stars with a comment the customer agreed to publish (16 §16.2 `GET /v1/reviews`). */
  publishable: z.boolean().default(false),
  publicName: z.string().optional(),
  createdAt: iso,
});
export type FeedbackDoc = z.infer<typeof feedbackDocSchema>;

export const SCORE_DIMENSIONS = ['branch', 'route', 'partner', 'driver'] as const;
export type ScoreDimension = (typeof SCORE_DIMENSIONS)[number];

/** `feedbackScores/{dimension}__{key}`: the daily roll-up (15 §15.3 step 6), over a rolling 90 days. */
export const feedbackScoreDocSchema = z.object({
  id: z.string(),
  dimension: z.enum(SCORE_DIMENSIONS),
  key: z.string(),
  name: z.string(),
  /** Where staff read it: a branch for branch and driver scores, a country otherwise. */
  branchId: branchCodeSchema.optional(),
  sectorId: sectorCodeSchema,
  windowDays: z.number().int().positive(),
  count: z.number().int().nonnegative(),
  average: z.number().min(0).max(5),
  /** How many ratings of 1, 2, 3, 4 and 5. */
  distribution: z.tuple([
    z.number().int(),
    z.number().int(),
    z.number().int(),
    z.number().int(),
    z.number().int(),
  ]),
  /** Satisfied (4–5) minus unhappy (1–3) as a percentage of ratings; a simple NPS-style number. */
  net: z.number(),
  lowCount: z.number().int().nonnegative(),
  openTickets: z.number().int().nonnegative(),
  computedAt: iso,
});
export type FeedbackScoreDoc = z.infer<typeof feedbackScoreDocSchema>;

// ---- Tickets (15 §15.3) -----------------------------------------------------------------------

export const TICKET_CATEGORIES = [
  'delay',
  'damage',
  'missing_item',
  'staff',
  'price',
  'partner',
  'other',
] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];
export const ticketCategorySchema = z.enum(TICKET_CATEGORIES);

export const TICKET_STATUSES = ['open', 'waiting', 'resolved', 'closed'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const TICKET_SOURCES = ['app', 'whatsapp', 'website', 'feedback', 'staff'] as const;

export const RESOLUTION_ACTIONS = [
  'apology',
  'voucher',
  'claim',
  'partner_warning',
  'other',
] as const;
export type ResolutionAction = (typeof RESOLUTION_ACTIONS)[number];

/** The SLA for a ticket: 24 hours (15 §15.3, 13). */
export const TICKET_SLA_HOURS = 24;

export const ticketMessageSchema = z
  .object({ from: z.enum(['customer', 'care']), text: z.string().max(2000), at: iso })
  .strict();

/**
 * `tickets/{tkt_…}`. The customer reads their own (the document holds nothing staff-only: notes
 * and the action log are in the staff-only subcollections `notes` and `actions`).
 */
export const ticketDocSchema = z.object({
  id: z.string().regex(/^tkt_[0-9A-HJKMNP-TV-Z]{26}$/),
  customerUid: customerUidSchema,
  customerId: customerIdSchema.optional(),
  customerName: z.string().max(120),
  phoneE164: e164Schema,
  category: ticketCategorySchema,
  subject: z.string().max(200),
  trackingNo: trackingNoSchema.optional(),
  feedbackId: z.string().optional(),
  rating: ratingSchema.optional(),
  source: z.enum(TICKET_SOURCES),
  status: z.enum(TICKET_STATUSES),
  /** The branch whose customer care handles it, and its country. */
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  assigneeUid: z.string().optional(),
  assigneeName: z.string().optional(),
  /** Created + 24 hours. The control tower's `ticket_open` clock runs against it. */
  slaDueAt: iso,
  stateSince: iso,
  /** The control tower looks at the ticket when this passes (13). */
  nextDueAt: iso.optional(),
  messages: z.array(ticketMessageSchema).max(200),
  resolution: z
    .object({
      action: z.enum(RESOLUTION_ACTIONS),
      /** What the customer is told. */
      summary: z.string().max(1000),
      voucherCode: z.string().optional(),
      at: iso,
    })
    .strict()
    .optional(),
  /** "Was it resolved?" after the resolution (15 §15.3 step 5). */
  followUp: z
    .object({
      askedAt: iso,
      answeredAt: iso.optional(),
      resolved: z.boolean().optional(),
    })
    .strict()
    .optional(),
  createdAt: iso,
  updatedAt: iso,
});
export type TicketDoc = z.infer<typeof ticketDocSchema>;

/** `tickets/{id}/notes/{n}`: staff only. */
export const ticketNoteDocSchema = z
  .object({ text: z.string().max(2000), byUid: z.string(), byName: z.string(), at: iso })
  .strict();

/** `tickets/{id}/actions/{n}`: the action log, staff only. */
export const ticketActionDocSchema = z
  .object({
    kind: z.enum([
      'opened',
      'assigned',
      'note',
      'customer_reply',
      'care_reply',
      'resolved',
      'reopened',
      'follow_up',
      'follow_up_answer',
    ]),
    byUid: z.string(),
    detail: z.string().max(1000),
    at: iso,
  })
  .strict();

export const openTicketInputSchema = z
  .object({
    clientRequestId: z.string().regex(/^[A-Za-z0-9_-]{8,40}$/),
    category: ticketCategorySchema,
    subject: z.string().trim().min(3).max(200),
    message: z.string().trim().min(3).max(2000),
    trackingNo: trackingNoSchema.optional(),
  })
  .strict();
export const openTicketResponseSchema = z
  .object({ ok: z.literal(true), ticketId: z.string() })
  .strict();

export const replyTicketInputSchema = z
  .object({
    clientRequestId: z.string().regex(/^[A-Za-z0-9_-]{8,40}$/),
    ticketId: z.string().regex(/^tkt_[0-9A-HJKMNP-TV-Z]{26}$/),
    message: z.string().trim().min(1).max(2000),
  })
  .strict();
export const replyTicketResponseSchema = z.object({ ok: z.literal(true) }).strict();

export const redeemVoucherInputSchema = z
  .object({
    clientRequestId: z.string().regex(/^[A-Za-z0-9_-]{8,40}$/),
    /** An entry of the rules' voucher catalogue. */
    catalogId: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/),
  })
  .strict();

/** What a customer sees of a voucher. */
export const voucherViewSchema = z
  .object({
    code: z.string(),
    label: z.string(),
    status: z.enum(['issued', 'applied', 'expired']),
    valueMinor: minor.nullable(),
    currency: z.string().nullable(),
    percent: z.number().nullable(),
    expiresAt: iso.nullable(),
  })
  .strict();
export const redeemVoucherResponseSchema = z
  .object({
    ok: z.literal(true),
    voucher: voucherViewSchema,
    balance: z.number().int(),
  })
  .strict();

// ---- Helpers shared by the server and the screens ---------------------------------------------

/** `+971 •••• 4567`: enough for the owner to recognise the number, not to use it. */
export function maskPhone(e164: string): string {
  const digits = e164.replace(/\D/g, '');
  const tail = digits.slice(-4);
  const country = countryOfPhone(e164)?.dial ?? e164.slice(0, Math.max(2, e164.length - 9));
  return `${country} •••• ${tail}`;
}
